import {useEffect, useMemo, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {appStyles} from "../../../../AppStyles.ts";
import type {ContentPage} from "../../../../sdo/ContentPage.ts";
import type {ContentSeries} from "../../../../sdo/ContentSeries.ts";
import ContentPageCard from "./content/ContentPageCard.tsx";
import ContentPageDetail from "./content/ContentPageDetail.tsx";

// TODO: Replace with API call to AResourceProfileManager.readContentSeries() + readContentSummary()
function buildTestData(): ContentSeries {
    return {
        uid: "cs-1",
        pageCount: 6,
        frames: [
            {
                uid: "f1", frameNumber: 1,
                page: 1, section: 1, paragraph: 1, sentence: 1,
                originalText: "Executive summary of the quarterly threat landscape report.",
                pointers: [{uid: "p1", typeKey: "user_profile", display: "Analyst Alpha"}],
            },
            {
                uid: "f2", frameNumber: 2,
                page: 1, section: 1, paragraph: 2, sentence: 1,
                originalText: "Key findings indicate increased activity in target sectors.",
                pointers: [],
            },
            {
                uid: "f3", frameNumber: 3,
                page: 3, section: 2, paragraph: 1, sentence: 3,
                originalText: "Cross-reference confirmed via DNS log correlation on segment 192.168.4.0/24.",
                pointers: [
                    {uid: "p2", typeKey: "user_profile", display: "Subject A"},
                    {uid: "p3", typeKey: "location", display: "Data Center East"},
                ],
            },
            {
                uid: "f4", frameNumber: 4,
                page: 5, section: 1, paragraph: 1, sentence: 1,
                originalText: "Appendix A: full timeline of observed events.",
                pointers: [],
            },
        ],
    };
}

function UXResourcePagesPanel() {
    const [contentSeries] = useState<ContentSeries>(buildTestData);
    const [selectedPageNum, setSelectedPageNum] = useState<number | null>(null);

    // Build page objects from page count
    const pages: ContentPage[] = useMemo(() => {
        return Array.from({length: contentSeries.pageCount}, (_, i) => ({
            pageNumber: i + 1,
            thumbnailUrl: null, // TODO: API endpoint for thumbnail
            renderUrl: null,    // TODO: API endpoint for full render
        }));
    }, [contentSeries]);

    // Auto-select first page when pages are available
    useEffect(() => {
        if (pages.length === 0) {
            if (selectedPageNum !== null) setSelectedPageNum(null);
            return;
        }
        const stillVisible = pages.some((p) => p.pageNumber === selectedPageNum);
        if (!stillVisible) {
            setSelectedPageNum(pages[0].pageNumber);
        }
    }, [pages, selectedPageNum]);

    const selectedPage = useMemo(() => {
        if (selectedPageNum === null) return null;
        return pages.find((p) => p.pageNumber === selectedPageNum) || null;
    }, [pages, selectedPageNum]);

    // Filter frames for the selected page
    const selectedFrames = useMemo(() => {
        if (selectedPageNum === null) return [];
        return contentSeries.frames.filter((f) => f.page === selectedPageNum);
    }, [contentSeries, selectedPageNum]);

    const handleSelect = (page: ContentPage) => {
        setSelectedPageNum(page.pageNumber);
    };

    const handleClose = () => {
        setSelectedPageNum(null);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Content Pages</Typography>
            </Stack>

            {/* Split layout: page grid (left) + detail pane (right, when selected) */}
            <Stack direction="row" spacing={0} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {/* Left: page thumbnail list (single column, vertical stack) */}
                <Box sx={{
                    flex: selectedPage ? "0 0 10%" : "1 1 100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    paddingRight: selectedPage ? "12px" : 0,
                    borderRight: selectedPage ? "1px solid #3F464F" : "none",
                    overflowY: "auto",
                }}>
                    <Box sx={{borderBottom: "1px solid #3F464F", paddingBottom: "6px", marginBottom: "10px"}}>
                        <Typography sx={{
                            color: "#a8b8c0",
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            textAlign: "left",
                        }}>
                            Select a Page
                        </Typography>
                        <Typography sx={{...appStyles.panelFilterCount, paddingLeft: 0, marginTop: "2px"}}>
                            {contentSeries.pageCount} {contentSeries.pageCount === 1 ? "page" : "pages"}
                        </Typography>
                    </Box>
                    <Stack direction="column" spacing={1}>
                        {pages.map((page) => (
                            <ContentPageCard
                                key={page.pageNumber}
                                page={page}
                                selected={selectedPageNum === page.pageNumber}
                                onClick={handleSelect}
                            />
                        ))}
                    </Stack>
                    {pages.length === 0 && (
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.4)",
                            fontSize: "0.80rem",
                            textAlign: "center",
                            padding: "24px",
                        }}>
                            No content pages found
                        </Typography>
                    )}
                </Box>

                {/* Right: detail pane (only when a page is selected) */}
                {selectedPage && (
                    <Box sx={{
                        flex: "1 1 90%",
                        minWidth: 300,
                        display: "flex",
                        flexDirection: "column",
                        paddingLeft: "12px",
                    }}>
                        <ContentPageDetail
                            page={selectedPage}
                            frames={selectedFrames}
                            onClose={handleClose}
                        />
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}

export default UXResourcePagesPanel;